import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, BellOff, Smartphone, Loader2, Info, Share2, PlusSquare, Check } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { syncPushSubscription } from "@/lib/pushSync";

// Iter-67: Push notifications card.
// * Shows subscribe / unsubscribe state for THIS browser
// * Exposes per-category toggles (task assigned, booking reminders, deal
//   status changes, quote accepted) synced to the backend
// * Lets the user pick reminder timings (15 / 30 / 60 / 1440 min)
// * "Send test push" button for immediate verification
//
// Iter-76: iOS Safari support hardened.
// * Web Push on iOS/iPadOS **only** works when the app has been added to
//   the Home Screen as a PWA AND the OS is 16.4 or newer. This card now
//   clearly walks the user through the install-then-return flow instead
//   of just disabling the button with a small note.

const OFFSET_OPTIONS = [
  { value: 15, label: "15 min before" },
  { value: 30, label: "30 min before" },
  { value: 60, label: "1 hour before" },
  { value: 1440, label: "1 day before" },
];

// Base64url → Uint8Array — required by PushManager.subscribe.
function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const isIos = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

// iOS gained Web Push in 16.4 — parse the version string so we can tell the
// user their OS is too old to receive push (they need to update iOS).
function iosVersion() {
  const m = navigator.userAgent.match(/OS (\d+)_(\d+)(?:_(\d+))?/);
  if (!m) return null;
  return { major: Number(m[1]), minor: Number(m[2] || 0) };
}
function iosSupportsPush() {
  const v = iosVersion();
  if (!v) return false;
  return v.major > 16 || (v.major === 16 && v.minor >= 4);
}

export default function PushNotificationsCard({ embedded = false }) {
  const qc = useQueryClient();
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: config } = useQuery({
    queryKey: ["push-config"],
    queryFn: async () => (await api.get("/push/config")).data,
  });

  const { data: prefs } = useQuery({
    queryKey: ["push-prefs"],
    queryFn: async () => (await api.get("/push/preferences")).data,
  });

  const savePrefs = useMutation({
    mutationFn: async (patch) => (await api.patch("/push/preferences", patch)).data,
    onSuccess: (d) => {
      qc.setQueryData(["push-prefs"], d);
      toast.success("Preferences updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });

  // Detect capability + current subscription state.
  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window;
    setSupported(ok);
    if (!ok) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
        // Re-sync the existing subscription to the server — heals the case
        // where the browser is subscribed but the server lost the record.
        if (sub) await syncPushSubscription();
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const subscribe = async () => {
    if (!config?.vapid_public_key) {
      toast.error("Server has no VAPID key configured");
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permission denied for notifications");
        return;
      }
      // Iter-76: iOS Safari can throw `NotAllowedError` if you call
      // `pushManager.subscribe` in the same tick that permission was
      // granted. A short delay lets Safari settle the permission state.
      if (isIos()) await new Promise((r) => setTimeout(r, 250));
      const reg = await navigator.serviceWorker.ready;
      let sub;
      try {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(config.vapid_public_key),
        });
      } catch (e) {
        // A stale subscription made with an old/different VAPID key blocks
        // re-subscribing — drop it and retry once.
        if (e?.name === "InvalidStateError") {
          const old = await reg.pushManager.getSubscription();
          if (old) await old.unsubscribe();
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(config.vapid_public_key),
          });
        } else {
          throw e;
        }
      }
      const raw = sub.toJSON();
      await api.post("/push/subscriptions", {
        endpoint: raw.endpoint,
        keys: raw.keys,
        expirationTime: raw.expirationTime || null,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Push notifications enabled on this browser");
    } catch (e) {
      // iOS surfaces subscription failures as generic DOMExceptions — spell
      // out the most common cases so the user knows what to do next.
      const name = e?.name || "";
      let msg = e?.message || "Could not enable push notifications";
      if (isIos() && !isStandalone()) {
        msg = "On iPhone, add CCFI Compass to your Home Screen first, then open it and try again.";
      } else if (name === "NotAllowedError") {
        msg = "The browser refused push permission. Check your iOS Settings → Notifications → CCFI Compass and re-enable.";
      } else if (name === "AbortError") {
        msg = "Could not talk to Apple's push service. Check your network and try again.";
      }
      toast.error(msg);
      // Also log the raw error so we can debug from browser dev tools.
      // eslint-disable-next-line no-console
      console.warn("[push] subscribe failed", e);
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscriptions", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push notifications disabled on this browser");
    } catch (e) {
      toast.error(e?.message || "Could not disable push");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test push sent — check your notification tray");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test push failed");
    }
  };

  const toggleOffset = (min) => {
    const cur = prefs?.reminder_offsets_min || [];
    const next = cur.includes(min) ? cur.filter((x) => x !== min) : [...cur, min];
    savePrefs.mutate({ ...prefs, reminder_offsets_min: next.length ? next : [30] });
  };

  const iosBlocked = isIos() && !isStandalone();
  const iosOldOs = isIos() && !iosSupportsPush();

  return (
    <Card className={embedded ? "rounded-sm border-0 shadow-none p-0" : "rounded-sm border-slate-200 industrial-shadow p-5"} data-testid="push-notifications-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-cyan-600" />
            <h3 className="font-display text-lg font-semibold text-slate-900">Push notifications</h3>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Get an instant alert when a task is assigned to you, a booking is coming up, a deal changes stage or a client accepts a quote.
          </p>
        </div>
        {subscribed ? (
          <Button variant="outline" onClick={unsubscribe} disabled={busy} className="rounded-sm" data-testid="push-unsubscribe-btn">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <BellOff size={14} className="mr-1" />}
            Turn off on this browser
          </Button>
        ) : (
          <Button onClick={subscribe} disabled={busy || !supported || iosBlocked || iosOldOs} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="push-subscribe-btn">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Bell size={14} className="mr-1" />}
            Enable push
          </Button>
        )}
      </div>

      {!supported && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900 flex items-start gap-2">
          <Info size={14} className="shrink-0 mt-0.5" />
          <span>This browser doesn't support the Web Push standard. Use a recent Chrome, Firefox, Edge or Safari.</span>
        </div>
      )}
      {iosOldOs && (
        <div className="mb-3 p-3 bg-rose-50 border border-rose-200 rounded-sm text-xs text-rose-900 flex items-start gap-2" data-testid="push-ios-old-os">
          <Info size={14} className="shrink-0 mt-0.5" />
          <span>
            <b>Update iOS to enable push.</b> Push notifications require iPhone/iPad running <b>iOS 16.4 or newer</b>. Go to Settings → General → Software Update on this device, then come back here.
          </span>
        </div>
      )}
      {iosBlocked && !iosOldOs && (
        <div className="mb-3 rounded-sm border border-cyan-200 bg-cyan-50/60 overflow-hidden" data-testid="push-ios-install">
          <div className="px-4 py-3 border-b border-cyan-200 bg-cyan-100/70 flex items-center gap-2">
            <Smartphone size={16} className="text-cyan-700" />
            <div className="text-sm font-semibold text-cyan-900">Install CCFI Compass on your iPhone first</div>
          </div>
          <div className="p-4 text-xs text-cyan-950 space-y-3">
            <p className="leading-relaxed">
              Apple only allows push notifications when a website is installed as an app on the Home Screen. It's a two-minute one-time setup — after that, alerts arrive on your lock screen exactly like a native app.
            </p>
            <ol className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">1</span>
                <span>In Safari, tap the <Share2 size={12} className="inline mx-0.5 -mt-0.5" /> <b>Share</b> button (bottom row of icons).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">2</span>
                <span>Scroll and tap <PlusSquare size={12} className="inline mx-0.5 -mt-0.5" /> <b>Add to Home Screen</b>, then <b>Add</b> in the top-right.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">3</span>
                <span>Open the new <b>CCFI Compass</b> icon from your Home Screen (not this Safari tab).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">4</span>
                <span>Go back to <b>Settings → Integrations</b> and tap <b>Enable push</b>. iOS will ask for permission — tap <b>Allow</b>.</span>
              </li>
            </ol>
            <div className="pt-2 border-t border-cyan-200 text-[11px] text-cyan-800 flex items-start gap-2">
              <Check size={12} className="shrink-0 mt-0.5" />
              <span>Only needed once per device. The Home Screen icon also gives you offline shell + faster launch.</span>
            </div>
          </div>
        </div>
      )}
      {permission === "denied" && (
        <div className="mb-3 p-3 bg-rose-50 border border-rose-200 rounded-sm text-xs text-rose-900">
          Notifications are blocked in this browser. Open the site permissions and re-allow notifications, then click Enable.
        </div>
      )}

      {subscribed && (
        <Button variant="ghost" size="sm" onClick={sendTest} className="mb-4 h-8 text-xs text-cyan-700 hover:text-cyan-800" data-testid="push-test-btn">
          Send a test push to this browser
        </Button>
      )}

      {prefs && (
        <div className="grid gap-4 border-t border-slate-100 pt-4">
          <div>
            <Label className="stamp-label text-slate-600 mb-2 block">What should we notify you about?</Label>
            <div className="grid sm:grid-cols-2 gap-y-2">
              <PrefRow label="Task assigned to me" value={prefs.task_assigned} onChange={(v) => savePrefs.mutate({ ...prefs, task_assigned: v })} testid="pref-task-assigned" />
              <PrefRow label="Task due today" value={prefs.task_due_today} onChange={(v) => savePrefs.mutate({ ...prefs, task_due_today: v })} testid="pref-task-due" />
              <PrefRow label="Booking reminders" value={prefs.booking_reminders} onChange={(v) => savePrefs.mutate({ ...prefs, booking_reminders: v })} testid="pref-booking" />
              <PrefRow label="Deal stage changes" value={prefs.deal_status_changes} onChange={(v) => savePrefs.mutate({ ...prefs, deal_status_changes: v })} testid="pref-deal-stage" />
              <PrefRow label="Client accepts a quote" value={prefs.quote_accepted} onChange={(v) => savePrefs.mutate({ ...prefs, quote_accepted: v })} testid="pref-quote-accepted" />
              <PrefRow label="Quote / PO pushed back to me" value={prefs.pushback} onChange={(v) => savePrefs.mutate({ ...prefs, pushback: v })} testid="pref-pushback" />
              <PrefRow label="Someone @mentions me in a deal note" value={prefs.mention} onChange={(v) => savePrefs.mutate({ ...prefs, mention: v })} testid="pref-mention" />
            </div>
          </div>

          <div>
            <Label className="stamp-label text-slate-600 mb-2 block">Remind me before scheduled events</Label>
            <div className="flex flex-wrap gap-2">
              {OFFSET_OPTIONS.map((o) => {
                const active = (prefs.reminder_offsets_min || []).includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggleOffset(o.value)}
                    className={`rounded-sm h-8 px-3 text-xs border transition ${active ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-cyan-400"}`}
                    data-testid={`pref-offset-${o.value}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">Applies to bookings and tasks with a due time. You can enable multiple.</p>
          </div>
        </div>
      )}
    </Card>
  );
}

function PrefRow({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}
